/**
 * React Query hooks。所有接口路径集中在 lib/api 下，这里只负责缓存与状态。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from './api/admin';
import * as authApi from './api/auth';
import * as sharesApi from './api/shares';
import * as studentsApi from './api/students';

export const queryKeys = {
  me: ['auth', 'me'] as const,
  captcha: ['auth', 'captcha'] as const,
  devices: ['auth', 'devices'] as const,
  students: ['students'] as const,
  student: (id: string) => ['students', id] as const,
  studentShares: (id: string) => ['students', id, 'shares'] as const,
  shares: ['shares'] as const,
  sharedStudents: ['shares', 'students'] as const,
  points: (id: string) => ['students', id, 'points'] as const,
  pointsLedger: (id: string, page: number) => ['students', id, 'points', 'ledger', page] as const,
  rewards: ['rewards'] as const,
};

// ---------------------------------------------------------------- 学生

export function useStudents(enabled = true) {
  return useQuery({
    queryKey: queryKeys.students,
    queryFn: () => studentsApi.listStudents(),
    enabled,
    staleTime: 30_000,
  });
}

export function useStudent(id: string | null) {
  return useQuery({
    queryKey: queryKeys.student(id ?? 'none'),
    queryFn: () => studentsApi.getStudent(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: studentsApi.CreateStudentPayload) => studentsApi.createStudent(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.students });
    },
  });
}

export function useUpdateStudent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: studentsApi.UpdateStudentPayload) =>
      studentsApi.updateStudent(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.students });
      void qc.invalidateQueries({ queryKey: queryKeys.student(id) });
    },
  });
}

export function useResetStudentPassword(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { newPassword?: string; forceChangeOnNextLogin?: boolean }) =>
      studentsApi.resetStudentPassword(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.student(id) });
    },
  });
}

// ---------------------------------------------------------------- 共享

export function useShares(enabled = true) {
  return useQuery({
    queryKey: queryKeys.shares,
    queryFn: () => sharesApi.listShares(),
    enabled,
  });
}

export function useSharedStudents(enabled = true) {
  return useQuery({
    queryKey: queryKeys.sharedStudents,
    queryFn: () => sharesApi.listSharedStudents(),
    enabled,
  });
}

export function useStudentShares(studentId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.studentShares(studentId ?? 'none'),
    queryFn: () => studentsApi.listStudentShares(studentId as string),
    enabled: Boolean(studentId) && enabled,
  });
}

export function useShareMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.shares });
    void qc.invalidateQueries({ queryKey: queryKeys.sharedStudents });
    // `['students']` 是**前缀**匹配（React Query 默认行为），因此它同时覆盖
    // `['students', id]` 与孩子详情页那两张共享名单 `['students', id, 'shares']`
    // —— 取消共享/发出邀请之后，刚失效的那条不会继续留在「已共享的家长」里。
    void qc.invalidateQueries({ queryKey: queryKeys.students });
  };

  return {
    accept: useMutation({ mutationFn: sharesApi.acceptShare, onSuccess: invalidate }),
    reject: useMutation({ mutationFn: sharesApi.rejectShare, onSuccess: invalidate }),
    // 取消共享与改权限都是嵌套路径，必须带上 studentId（后端就是这么注册的）
    cancel: useMutation({
      mutationFn: (vars: { studentId: string; shareId: string }) =>
        sharesApi.cancelShare(vars.studentId, vars.shareId),
      onSuccess: invalidate,
    }),
    updatePermission: useMutation({
      mutationFn: (vars: {
        studentId: string;
        shareId: string;
        permission: sharesApi.SharePermissionLevel;
      }) => sharesApi.updateSharePermission(vars.studentId, vars.shareId, vars.permission),
      onSuccess: invalidate,
    }),
    create: useMutation({
      mutationFn: (vars: {
        studentId: string;
        targetUsername?: string;
        targetEmail?: string;
        permission: sharesApi.SharePermissionLevel;
      }) => sharesApi.createShare(vars),
      onSuccess: invalidate,
    }),
  };
}

// ---------------------------------------------------------------- 学生端今日任务 / 积分
//
// 说明：本批已把"今日打卡"与"积分流水"迁移到 lib/domainQueries.ts
// （那边有更完整的形状规整与"接口未就绪"三态）。这里只保留孩子详情页需要的
// **余额**查询 —— 它要的只是一个大数字，不必走完整版。

export function useStudentPoints(studentId: string | null) {
  return useQuery({
    queryKey: queryKeys.points(studentId ?? 'none'),
    queryFn: () => studentsApi.studentPoints(studentId as string),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

// ---------------------------------------------------------------- 认证辅助

export function useDevices(enabled = true) {
  return useQuery({
    queryKey: queryKeys.devices,
    queryFn: () => authApi.listDevices(),
    enabled,
    retry: 1,
  });
}

export function useRevokeDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => authApi.revokeDevice(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices });
    },
  });
}

/**
 * 批量移除"其它全部设备"。当前设备由服务端从 Cookie 排除，前端只负责触发与提示。
 * 成功后必须重新拉一次列表：被移除的会话要从界面上消失。
 */
export function useRevokeOtherDevices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.revokeOtherDevices(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices });
    },
  });
}

/** 清理本账号的历史失效会话（只删死会话，活会话一条不删）。 */
export function useCleanupSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.cleanupSessions(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices });
    },
  });
}

/** 学生端界面模式的最终判定（家长覆盖 > 后端 > 年级）在 lib/api/students.ts 里实现。 */
export { resolveUiMode } from './api/students';

// ---------------------------------------------------------------- 系统管理后台（见方案 2B）

export const adminQueryKeys = {
  overview: ['admin', 'overview'] as const,
  users: (query: adminApi.AdminUserQuery) => ['admin', 'users', query] as const,
  settings: ['admin', 'settings'] as const,
  audit: (query: Record<string, unknown>) => ['admin', 'audit', query] as const,
  backup: ['admin', 'backup'] as const,
};

export function useAdminOverview(enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.overview,
    queryFn: adminApi.getOverview,
    enabled,
    // 看板是"实时"信息，但没必要每切一次页面就打一次主机信息
    staleTime: 15_000,
  });
}

export function useAdminUsers(query: adminApi.AdminUserQuery, enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.users(query),
    queryFn: () => adminApi.listUsers(query),
    enabled,
    // 换页/搜索时保留上一页内容，避免表格闪成空白
    placeholderData: (prev) => prev,
  });
}

export function useAdminSettings(enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.settings,
    queryFn: adminApi.getSettings,
    enabled,
  });
}

export function useAdminAudit(query: Record<string, unknown>, enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.audit(query),
    queryFn: () => adminApi.listAudit(query as Parameters<typeof adminApi.listAudit>[0]),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useAdminBackupStatus(enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.backup,
    queryFn: adminApi.getBackupStatus,
    enabled,
    // 备份状态变化很慢，且探测有超时；不要频繁重试
    staleTime: 60_000,
    retry: 0,
  });
}

/**
 * 账号管理动作（踢下线 / 停用启用）。
 *
 * ⚠️ 这里**没有** `resetPassword`：管理员不再能重置任何人的密码。
 * 家长忘记密码自己走 `/forgot`（邮箱自助重置），孩子由家长在孩子详情页重置，
 * 运维的紧急恢复走服务器上的 `operator.ts reset-password`。
 * 前端不留"点了会 404"的死代码 —— 后端那条路由也已经整条删掉了。
 */
export function useAdminUserMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    void qc.invalidateQueries({ queryKey: adminQueryKeys.overview });
  };

  return {
    revokeSessions: useMutation({ mutationFn: adminApi.revokeUserSessions, onSuccess: invalidate }),
    setStatus: useMutation({
      mutationFn: (vars: { id: string; disabled: boolean }) =>
        adminApi.setUserStatus(vars.id, vars.disabled),
      onSuccess: invalidate,
    }),
  };
}

export function useAdminSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminApi.updateSettings,
    onSuccess: (data) => {
      qc.setQueryData(adminQueryKeys.settings, data);
    },
  });
}

export function useAdminInviteCode() {
  return useMutation({ mutationFn: (ttlMinutes: number) => adminApi.createInviteCode(ttlMinutes) });
}

export function useAdminMailTest() {
  return useMutation({
    mutationFn: (vars: { email: string; note?: string }) =>
      adminApi.sendMailTest(vars.email, vars.note ?? ''),
  });
}
